import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { callUIAPI } from '../../../utils/apiUtils';
import * as apiConstants from '../../../utils/apiUtilsConstants';
import './SupportRequest.css';

const STATUS_COLORS = {
  pending:     { bg: '#fef9c3', text: '#854d0e' },
  in_progress: { bg: '#dbeafe', text: '#1e40af' },
  resolved:    { bg: '#dcfce7', text: '#166534' },
  closed:      { bg: '#f3f4f6', text: '#6b7280' },
};

const STATUS_LABELS = {
  pending: 'Pending',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

const TRIGGER_TYPE_LABELS = {
  confidence_based: 'Low Confidence',
  user_requested: 'User Requested',
};

const SupportRequest = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editingStatus, setEditingStatus] = useState(false);
  const [statusValue, setStatusValue] = useState('');
  const [notesValue, setNotesValue] = useState('');
  const [resolutionNotesValue, setResolutionNotesValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const fetchRequest = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await callUIAPI(`bo/support-requests/${id}`, null, apiConstants.METHOD_GET, true);
      const item = response.data.item;
      setRequest(item);
      setStatusValue(item.status);
      setNotesValue(item.notes || '');
      setResolutionNotesValue(item.resolutionNotes || '');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load support request.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) fetchRequest();
  }, [id, fetchRequest]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveError(null);
      setSaveSuccess(false);

      const response = await callUIAPI(
        `bo/support-requests/${id}`,
        {
          status: statusValue,
          notes: notesValue,
          resolutionNotes: resolutionNotesValue,
        },
        apiConstants.METHOD_PUT,
        true
      );

      setRequest(response.data.item);
      setEditingStatus(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err?.response?.data?.message || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="sr-page">
        <div className="sr-card">
          <div className="sr-skeleton-title" />
          <div className="sr-skeleton-line" />
          <div className="sr-skeleton-line-short" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sr-page">
        <div className="sr-card sr-error-card">
          <p className="sr-error-text">{error}</p>
          <button className="sr-btn-retry" onClick={fetchRequest}>Retry</button>
        </div>
      </div>
    );
  }

  if (!request) return null;

  const statusColors = STATUS_COLORS[request.status] || { bg: '#f3f4f6', text: '#374151' };
  const conversation = (() => {
    try {
      if (typeof request.conversationHistory === 'string') {
        return JSON.parse(request.conversationHistory);
      }
      return Array.isArray(request.conversationHistory) ? request.conversationHistory : [];
    } catch {
      return [];
    }
  })();

  const confidenceData = (() => {
    try {
      if (typeof request.confidenceAssessment === 'string') {
        return JSON.parse(request.confidenceAssessment);
      }
      return request.confidenceAssessment || {};
    } catch {
      return {};
    }
  })();

  return (
    <div className="sr-page">
      {}
      <button className="sr-back-link" onClick={() => navigate('/bo/support-requests')}>
        ← Back to Support Requests
      </button>

      <div className="sr-card">
        {}
        <div className="sr-header">
          <div>
            <h2 className="sr-title">Support Request #{request.id}</h2>
            <span
              className="sr-status-badge"
              style={{ backgroundColor: statusColors.bg, color: statusColors.text }}
            >
              {STATUS_LABELS[request.status] || request.status}
            </span>
          </div>
          <span className="sr-meta">
            {new Date(request.createdOn).toLocaleString()}
          </span>
        </div>

        <div className="sr-divider" />

        {}
        <div className="sr-section">
          <h3 className="sr-section-label">User</h3>
          <div className="sr-info-grid">
            <div>
              <span className="sr-info-label">Name</span>
              <span className="sr-info-value">{request.userName}</span>
            </div>
            <div>
              <span className="sr-info-label">Email</span>
              <span className="sr-info-value">
                <a href={`mailto:${request.userEmail}`}>{request.userEmail}</a>
              </span>
            </div>
            <div>
              <span className="sr-info-label">Firm ID</span>
              <span className="sr-info-value">{request.firmId}</span>
            </div>
            <div>
              <span className="sr-info-label">Trigger Type</span>
              <span className="sr-info-value">
                {TRIGGER_TYPE_LABELS[request.triggerType] || request.triggerType}
              </span>
            </div>
          </div>
        </div>

        <div className="sr-divider" />

        {}
        <div className="sr-section">
          <h3 className="sr-section-label">Trigger Question</h3>
          <div className="sr-trigger-box">
            {request.triggerQuestion}
          </div>
        </div>

        {}
        {confidenceData.confidence_score != null && (
          <>
            <div className="sr-divider" />
            <div className="sr-section">
              <h3 className="sr-section-label">Confidence Assessment</h3>
              <div className="sr-confidence-box">
                <div className="sr-confidence-score">
                  {confidenceData.confidence_score}/10
                  <span className="sr-confidence-level">
                    ({confidenceData.confidence_level || 'N/A'})
                  </span>
                </div>
                {confidenceData.reasoning && (
                  <p className="sr-confidence-reasoning">{confidenceData.reasoning}</p>
                )}
                {Array.isArray(confidenceData.concerns) && confidenceData.concerns.length > 0 && (
                  <p className="sr-confidence-concerns">
                    <strong>Concerns:</strong> {confidenceData.concerns.join('; ')}
                  </p>
                )}
              </div>
            </div>
          </>
        )}

        <div className="sr-divider" />

        {}
        <div className="sr-section">
          <h3 className="sr-section-label">Conversation ({conversation.length} messages)</h3>
          <div className="sr-conversation">
            {conversation.length === 0 ? (
              <p className="sr-empty-text">No conversation history.</p>
            ) : (
              conversation.map((msg, idx) => (
                <div
                  key={idx}
                  className={`sr-message sr-message--${msg.role}`}
                >
                  <div className="sr-message-label">
                    {msg.role === 'user' ? 'User' : 'Assistant'}
                  </div>
                  <div className="sr-message-content">{msg.content}</div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="sr-divider" />

        {}
        <div className="sr-section">
          <h3 className="sr-section-label">Manage Request</h3>

          {saveSuccess && (
            <div className="sr-save-success">Changes saved successfully.</div>
          )}
          {saveError && (
            <div className="sr-save-error">{saveError}</div>
          )}

          <div className="sr-form-group">
            <label className="sr-form-label">Status</label>
            <select
              className="sr-form-select"
              value={statusValue}
              onChange={(e) => {
                setStatusValue(e.target.value);
                setEditingStatus(true);
              }}
              disabled={saving}
            >
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          <div className="sr-form-group">
            <label className="sr-form-label">Internal Notes</label>
            <textarea
              className="sr-form-textarea"
              value={notesValue}
              onChange={(e) => {
                setNotesValue(e.target.value);
                setEditingStatus(true);
              }}
              placeholder="Add internal notes about this request…"
              rows={3}
              disabled={saving}
            />
          </div>

          <div className="sr-form-group">
            <label className="sr-form-label">Resolution Notes</label>
            <textarea
              className="sr-form-textarea"
              value={resolutionNotesValue}
              onChange={(e) => {
                setResolutionNotesValue(e.target.value);
                setEditingStatus(true);
              }}
              placeholder="Describe how this was resolved…"
              rows={3}
              disabled={saving}
            />
          </div>

          <div className="sr-form-actions">
            <button
              className="sr-btn sr-btn-save"
              onClick={handleSave}
              disabled={!editingStatus || saving}
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>

        {}
        {request.resolvedAt && (
          <p className="sr-resolved-at">
            Resolved at: {new Date(request.resolvedAt).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
};

export default SupportRequest;